import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, AtomBadge, StatusPill, EmptyState } from "../components/primitives";
import { formatNGN, formatDateTime } from "../lib/format";
import { Search, X } from "lucide-react";
import { Link } from "react-router-dom";

const CHANNELS = ["Mobile app","USSD","Internet banking","ATM","POS","Branch","NIBSS Instant Payment","RTGS","Cheque","SWIFT"];

export default function Transactions() {
  const [data, setData] = useState({ transactions: [], total: 0 });
  const [filters, setFilters] = useState({ direction: "", channel: "", status: "", min_risk: 0 });
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("Detail");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "100" });
    if (filters.direction) params.set("direction", filters.direction);
    if (filters.channel) params.set("channel", filters.channel);
    if (filters.status) params.set("status", filters.status);
    if (filters.min_risk) params.set("min_risk", filters.min_risk);
    const r = await api.get(`/transactions?${params}`);
    setData(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [filters]);

  // Stats on slice
  const stats = {
    count: data.transactions.length,
    volume: data.transactions.filter(t=>t.currency==='NGN').reduce((s,t)=>s+t.amount,0),
    alerts: data.transactions.filter(t=>t.alert_status!=='Cleared').length,
    avgRisk: data.transactions.length ? Math.round(data.transactions.reduce((s,t)=>s+t.risk_score,0)/data.transactions.length) : 0,
    maxRisk: data.transactions.length ? Math.max(...data.transactions.map(t=>t.risk_score)) : 0,
  };

  return (
    <div>
      <PageHeader
        title="Transaction Monitoring"
        subtitle={`${data.total.toLocaleString()} transactions evaluated in current view · live stream`}
        sections={["5.5","5.6"]}
        atom={<AtomBadge model="Atom Proton" version="v2.4" description="Live transaction scoring · sub-second" latency="84ms p95" />}
      />

      <div className="px-6 py-2 bg-bg-surface border-b border-line">
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <div className="flex items-center gap-1">
            <span className="text-ink-subtle">View:</span>
            {[["","All transactions"],["Pending review","Alerts only"],["In case","In case"],["Escalated","Escalated"]].map(([v,l]) => (
              <button key={v} onClick={() => setFilters({...filters, status: v})} className={`px-2 py-0.5 rounded-sm border ${filters.status===v?'bg-navy text-white border-navy':'border-line hover:bg-bg-hover'}`}>{l}</button>
            ))}
          </div>
          <div className="border-l border-line pl-2 flex items-center gap-1">
            <span className="text-ink-subtle">Channel:</span>
            <select onChange={(e) => setFilters({...filters, channel: e.target.value})} className="px-1.5 py-0.5 border border-line rounded-sm bg-bg-surface">
              <option value="">All</option>{CHANNELS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="border-l border-line pl-2 flex items-center gap-1">
            <span className="text-ink-subtle">Direction:</span>
            {["","Credit","Debit"].map(d => (
              <button key={d} onClick={() => setFilters({...filters, direction: d})} className={`px-2 py-0.5 rounded-sm border ${filters.direction===d?'bg-navy text-white border-navy':'border-line hover:bg-bg-hover'}`}>{d || "Both"}</button>
            ))}
          </div>
          <div className="border-l border-line pl-2 flex items-center gap-1">
            <span className="text-ink-subtle">Min risk:</span>
            <input type="range" min="0" max="100" value={filters.min_risk} onChange={(e) => setFilters({...filters, min_risk: parseInt(e.target.value)})} className="w-24" />
            <span className="tabular w-8">{filters.min_risk}</span>
          </div>
        </div>

        {/* KPI strip */}
        <div className="flex items-center gap-4 mt-2 text-[11px]">
          <div><span className="text-ink-subtle">In view</span> <span className="font-medium tabular">{stats.count.toLocaleString()}</span></div>
          <div><span className="text-ink-subtle">Volume</span> <span className="font-medium tabular">{formatNGN(stats.volume, {short:true})}</span></div>
          <div><span className="text-ink-subtle">Alerts</span> <span className="font-medium tabular">{stats.alerts}</span></div>
          <div><span className="text-ink-subtle">Avg risk</span> <span className="font-medium tabular">{stats.avgRisk}</span></div>
          <div><span className="text-ink-subtle">Max risk</span> <span className="font-medium tabular">{stats.maxRisk}</span></div>
        </div>
      </div>

      <div className="p-6">
        <Card>
          {loading ? <div className="p-6 text-[13px] text-ink-muted">Loading transaction stream…</div> : (
            <div className="overflow-x-auto">
              <table className="dense w-full tabular">
                <thead>
                  <tr>
                    <th className="w-6"></th><th>Timestamp</th><th>Customer</th><th>Direction</th><th>Channel</th>
                    <th className="text-right">Amount</th><th>Counterparty</th><th>Bank</th><th>Risk</th><th>Rules</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.slice(0,100).map(t => (
                    <tr key={t.id} data-testid={`txn-row-${t.id}`} onClick={() => { setSelected(t); setTab("Detail"); }} className={`cursor-pointer ${selected?.id === t.id ? 'bg-amber/5' : ''}`}>
                      <td>{t.risk_score >= 60 && <span className="text-risk-high">⚠</span>}</td>
                      <td className="text-[11px]">{formatDateTime(t.ts)}</td>
                      <td className="text-[12px]"><span className="font-medium">{t.customer_name}</span><div className="text-[10px] text-ink-subtle">{t.customer_id}</div></td>
                      <td className="text-[11px]">{t.direction === 'Credit' ? '↓' : '↑'} {t.direction}</td>
                      <td className="text-[11px]">{t.channel}</td>
                      <td className="text-right">{t.currency === 'NGN' ? formatNGN(t.amount) : `${t.currency} ${t.amount.toLocaleString()}`}</td>
                      <td className="text-[12px]">{t.counterparty}</td>
                      <td className="text-[11px] text-ink-subtle">{t.counterparty_bank}</td>
                      <td><span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${t.risk_score>=80?'bg-risk-veryhigh/15 text-risk-veryhigh':t.risk_score>=60?'bg-risk-high/15 text-risk-high':t.risk_score>=40?'bg-risk-medium/15 text-risk-medium':'bg-risk-low/15 text-risk-low'}`}>{t.risk_score}</span></td>
                      <td className="text-[10px] text-ink-subtle">{t.rules_fired.length > 0 ? `${t.rules_fired.length} rule${t.rules_fired.length>1?'s':''}` : '—'}</td>
                      <td><StatusPill status={t.alert_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.transactions.length === 0 && <EmptyState />}
            </div>
          )}
        </Card>
      </div>

      {/* Side panel */}
      {selected && (
        <div className="fixed inset-0 bg-navy-900/30 z-40" onClick={() => setSelected(null)}>
          <div className="absolute right-0 top-0 bottom-0 w-[520px] bg-bg-surface border-l border-line overflow-y-auto animate-slide-in-right" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-line flex items-center justify-between">
              <div>
                <div className="text-[14px] font-medium">{selected.id}</div>
                <div className="text-[11px] text-ink-subtle">{formatDateTime(selected.ts)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 hover:bg-bg-hover rounded"><X size={16} /></button>
            </div>

            <div className="flex border-b border-line">
              {["Detail","Reasoning","Action"].map(t => (
                <button key={t} data-testid={`side-tab-${t.toLowerCase()}`} onClick={() => setTab(t)} className={`px-4 py-2 text-[12px] border-b-2 -mb-px ${tab===t?'border-amber text-ink font-medium':'border-transparent text-ink-muted'}`}>{t}</button>
              ))}
            </div>

            <div className="p-4">
              {tab === "Detail" && (
                <div className="space-y-2 text-[12px]">
                  {[
                    ["Customer", <Link to={`/customers/${selected.customer_id}`} className="text-amber-600 hover:underline">{selected.customer_name}</Link>],
                    ["Account", selected.account_no],
                    ["Direction", selected.direction],
                    ["Channel", selected.channel],
                    ["Amount", selected.currency === 'NGN' ? formatNGN(selected.amount) : `${selected.currency} ${selected.amount.toLocaleString()}`],
                    ["Counterparty", selected.counterparty],
                    ["Counterparty bank", selected.counterparty_bank],
                    ["Counterparty account", selected.counterparty_account],
                    ["Narration", selected.narration],
                    ["Currency", selected.currency],
                  ].map(([k,v]) => <div key={k} className="flex justify-between gap-3 border-b border-line/50 py-1.5"><span className="text-ink-subtle">{k}</span><span className="text-right">{v}</span></div>)}
                </div>
              )}
              {tab === "Reasoning" && (
                <div className="space-y-4">
                  <div>
                    <div className="text-[11px] font-medium text-ink-muted uppercase tracking-wider mb-2">Rule contributions</div>
                    {selected.rules_fired.length > 0 ? selected.rules_fired.map((r, i) => (
                      <div key={i} className="border border-line rounded-sm p-2.5 mb-1.5">
                        <div className="flex items-center justify-between">
                          <div className="text-[12px] font-medium">{r}</div>
                          <span className="text-[10px] px-1.5 py-0.5 bg-risk-high/15 text-risk-high rounded">FAIL</span>
                        </div>
                        <div className="text-[11px] text-ink-subtle mt-1">Threshold engaged · customer observed value exceeds policy bound</div>
                        <div className="text-[10px] text-ink-subtle mt-1">CBN 5.5 · AMLTRIX {selected.typology || 'T1003'}</div>
                      </div>
                    )) : <div className="text-[12px] text-ink-subtle">No rules fired on this transaction.</div>}
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-ink-muted uppercase tracking-wider mb-2">Model contributions · Atom Proton v2.4</div>
                    {[
                      ["Velocity vs baseline (24h)", 18],["Counterparty risk", 12],["Hour-of-day anomaly", 7],
                      ["Geographic anomaly", 6],["Amount above p95", 5],["Channel anomaly", 4],["Beneficiary newly added", 3],["Base score", Math.max(8, selected.risk_score - 55)],
                    ].map(([k,v]) => (
                      <div key={k} className="mb-1.5">
                        <div className="flex justify-between text-[11px]"><span>{k}</span><span className="tabular">+{v}</span></div>
                        <div className="h-1 bg-bg-subtle rounded-sm"><div className="h-full bg-navy rounded-sm" style={{width: `${Math.min(100, v*5)}%`}} /></div>
                      </div>
                    ))}
                    <div className="flex justify-between text-[12px] font-medium pt-2 border-t border-line mt-2"><span>Total score</span><span className="tabular">{selected.risk_score}</span></div>
                  </div>
                  <div className="border border-amber/30 bg-amber/4 rounded-sm p-3">
                    <div className="text-[11px] font-medium text-ink-muted uppercase tracking-wider mb-1">Atom Proton narrative</div>
                    <div className="text-[12px] leading-relaxed">
                      This transaction {selected.risk_score >= 60 ? 'triggered an alert because' : 'cleared because'} the customer {selected.customer_name} has activity {selected.risk_score >= 60 ? 'inconsistent with the 90-day baseline' : 'consistent with the 90-day baseline'}. {selected.typology && `Pattern matches AMLTRIX ${selected.typology}.`} Atom Proton v2.4 confidence: 0.{selected.risk_score >= 80 ? '94' : selected.risk_score >= 60 ? '78' : '32'}.
                    </div>
                    <div className="text-[10px] text-ink-subtle mt-2">CBN 5.5 · FATF Rec. 20 · Explainability 0.91 (Atom Photon v1.0)</div>
                  </div>
                </div>
              )}
              {tab === "Action" && (
                <div className="space-y-2">
                  <button className="w-full px-3 py-2 text-[13px] bg-navy text-white rounded-sm hover:bg-navy-700 text-left">Create case</button>
                  <button className="w-full px-3 py-2 text-[13px] border border-line rounded-sm hover:bg-bg-hover text-left">Escalate to MLRO</button>
                  <button className="w-full px-3 py-2 text-[13px] border border-line rounded-sm hover:bg-bg-hover text-left">Clear as false positive</button>
                  <button className="w-full px-3 py-2 text-[13px] border border-line rounded-sm hover:bg-bg-hover text-left">Add note</button>
                  <button className="w-full px-3 py-2 text-[13px] border border-line rounded-sm hover:bg-bg-hover text-left">Suppress similar (maker-checker)</button>
                  <Link to="/atom-explainer" className="block w-full px-3 py-2 text-[13px] border border-amber/40 bg-amber/8 text-amber-600 rounded-sm hover:bg-amber/15 text-left">Open in Atom Explainer →</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
